import { type ReactNode, forwardRef } from "react";
import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";

import { Spinner } from "./Spinner";

type ButtonProps = React.ComponentPropsWithoutRef<"button"> & {
  children: ReactNode;
  fullWidth?: boolean;
  variant?: "primary" | "outline" | "danger" | "outline-bordered";
  size?: "small" | "medium" | "large" | "x-small";
  isLoading?: boolean;
  asChild?: boolean;
  borderRadius?: "rounded" | "no-rounded";
  borderColor?: string;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, forwardRef) => {
    const {
      fullWidth = false,
      variant = "primary",
      size = "medium",
      borderRadius = "rounded-lg",
      children,
      className,
      isLoading = false,
      asChild,
      borderColor = "border-transparent",
      ...rest
    } = props;

    const Component = asChild ? Slot : ("button" as "button");

    return (
      <Component
        ref={forwardRef}
        className={clsx(
          {
            "bg-primary-light text-white hover:bg-secondary-dark disabled:bg-[#C9D8E1] disabled:text-[#ADCADA]":
              variant === "primary",
            "border-primary-light text-primary-light hover:bg-[#C9D8E1] disabled:bg-transparent disabled:border-[#C9D8E1] disabled:text-[#ADCADA]":
              variant === "outline",
            "border-primary-light text-primary-light hover:bg-[#e25050] hover:text-white disabled:bg-transparent disabled:border-[#e25050] disabled:text-[#ADCADA]":
              variant === "danger",
            "py-[6px] h-8": size === "small",
            "py-[11px] h-10": size === "medium",
            "py-[14px] h-12": size === "large",
            "p-0 m-0 h-6 w-6": size === "x-small",
            "w-full": fullWidth,
            "w-fit": !fullWidth,
            "rounded-none": borderRadius === "no-rounded",
          },
          borderColor,
          className,
          "px-4 rounded-lg border-1 transition-colors text-sm font-medium lg:text-base items-center flex justify-center gap-2"
        )}
        {...rest}
        disabled={isLoading || rest.disabled}
      >
        {isLoading && <Spinner size="small" />}
        {!isLoading && children}
      </Component>
    );
  }
);

Button.displayName = "Button";

export { Button };
